"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardCheck, PackageOpen, SearchCheck, Sparkles, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  title: "The Concept",
  description: "We begin with an ethereal vision, translating your dreams into a cohesive aesthetic narrative that captures the soul of your celebration.",
  icon: ClipboardCheck
}, {
  title: "The Curation",
  description: "Our artisans hand-select every element, from moonlit lavender linens to signature silver accents, ensuring a bespoke atmosphere of luxury.",
  icon: PackageOpen
}, {
  title: "The Execution",
  description: "With graceful fluidity, our team orchestrates the event in real-time, managing every delicate detail so you can remain fully present.",
  icon: Sparkles
}, {
  title: "The Signature",
  description: "A final, meticulous walkthrough ensures our high-fashion standards are surpassed, leaving only the lingering magic of a perfect occasion.",
  icon: SearchCheck
}];
export default function TheSignatureExperience() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const currentScrollPosition = windowHeight / 2 - rect.top;
      let percentage = currentScrollPosition / totalScrollableDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222853" id="the-signature-experience" ref={sectionRef} className="dark bg-background text-foreground relative min-h-screen">
      {}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border py-4 px-6 shadow-sm">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-default font-medium tracking-widest uppercase text-foreground">
              The Signature Experience
            </h2>
            <span className="text-sm font-mono text-primary">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-1.5 bg-secondary" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="text-center mb-24">
          <h3 className="text-3xl md:text-6xl font-serif font-light text-foreground mb-6 tracking-tight">
            The Art of the <br className="hidden md:block" /> Atmosphere.
          </h3>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default tracking-wide">
            Discover our poetic approach to event planning. A four-step journey from initial inspiration to the final, breathtaking signature.
          </p>
        </div>

        {}
        <div className="relative border-l-2 border-secondary ml-4 md:ml-12 space-y-32 pb-24">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative pl-8 md:pl-20 min-h-[30vh] flex flex-col justify-center group">
                {}
                <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 size-5 rounded-lg bg-background border-2 border-primary transition-transform duration-700 group-hover:scale-125 shadow-lg shadow-primary/20" />
                
                {}
                <span className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 text-[8rem] md:text-[14rem] font-serif font-bold text-primary/10 select-none leading-none pointer-events-none transition-colors duration-700 group-hover:text-primary/20">
                  {i + 1}
                </span>

                <Card className="relative z-10 border-none bg-transparent overflow-visible">
                  <CardHeader className="px-0 pt-0 pb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 rounded-lg bg-accent text-primary shadow-md border border-secondary">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-2xl md:text-4xl font-serif font-medium text-foreground tracking-tight">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl font-default tracking-wide">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center relative z-20">
          <Button asChild size="lg" className="rounded-md px-10 bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-500 shadow-xl">
            <Link to="/LetsTalkPage">
              Book a Consultation
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}